import { useEffect, useRef } from 'react'
import { ChatContextProvider, registerChatContext } from './chatRequestContext'

/**
 * Contribute this screen's context to the chat for as long as the component is
 * mounted, and remove it on unmount.
 *
 * The provider is held in a ref so a fresh closure each render does not
 * re-register — the registration depends on `key` alone, while the value the
 * chat reads is always the latest one.
 *
 * Not directly unit-tested: `packages/common` resolves a second copy of React
 * from its own `node_modules`, so rendering anything from inside this package
 * throws `useContext of null` (the same pre-existing failure as
 * `EllipsisText.spec.tsx`). The registration and identity-guarded removal this
 * wraps are covered in `chatRequestContext.spec.ts`.
 */
export const useChatContext = (
  key: string,
  provider: ChatContextProvider
): void => {
  const latest = useRef(provider)
  latest.current = provider

  useEffect(() => registerChatContext(key, () => latest.current()), [key])
}
